/* eslint-disable react/jsx-props-no-spreading */
import AddIcon from "@mui/icons-material/Add"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import SearchIcon from "@mui/icons-material/Search"
import { AlertTitle, Box, Collapse, Divider, Stack } from "@mui/material"
import { ReactElement, useContext, useEffect, useState } from "react"
import { FormProvider, useFieldArray } from "react-hook-form"
import { useSearchParams } from "react-router-dom"

import {
    ApplicationClasses,
    Datasets,
    NEW_AND_EXISTING_IN_DATABASE,
} from "../../../../constants/enums"
import {
    SINGLE_PHASE_ASSUMED,
    SPLIT_PHASE_ASSUMED,
    SUPPLEMENTING_INFO_KW,
    THREE_PHASE_ASSUMED,
} from "../../../../constants/generalConstants"
import {
    TT_ADVANCED_SEARCH,
    TT_CANT_SEE_LCT,
    TT_DEVICE_SEARCH,
    TT_ENTER_MANUALLY,
    TT_SELECT_LCT,
} from "../../../../constants/tooltips"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { useScreenSize } from "../../../../hooks/useScreenSize"
import { SINGLE_GEN_DEVICE_NAV_LINK } from "../../../../router/routes"
import useSubmitDraft from "../../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import { ISelectDeviceFormSpec } from "../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { IDeviceSearchResult } from "../../../../services/middleware/types/singleApplication/searchLctTypes"
import { INPUT_HELP_TOOLTIP_SPACING } from "../../../../themes/defaultThemeParams"
import {
    EXISTING_IN_DATABASE_KEY,
    EXISTING_NOT_IN_DATABASE_KEY,
    IN_DATABASE_KEY,
    NOT_IN_DATABASE_KEY,
    SELECT_MANUAL_OPTION_EXISTING,
    SELECT_MANUAL_OPTION_NEW,
    applicationIsAdmdCompliant,
    buildDeviceClassDispatcher,
    getDeviceTypesForSelectedLcts,
    isV2gEvcpContext,
    prepareFormProperties,
    processUserAccountDerivedInputs,
    scrollToTop,
} from "../../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../../buttons/appButton"
import AppAlert from "../../../dataDisplay/alerts/appAlert"
import DeviceSearchDialog from "../../../deviceSearch/deviceSearchDialog"
import FormErrors from "../../../formErrors/formErrors"
import AppTooltip from "../../../helper/appTooltip"
import HelpIcon from "../../../helper/helpIcon"
import ValidatedAutocomplete from "../../../inputs/validated/validatedSelect"
import DisplayIf from "../../../wrappers/displayIf"
import ArrayFormErrorDisplay from "../alerts/arrayFormErrorDisplay"
import ArrayFormGlobalErrorDisplay from "../alerts/arrayFormGlobalErrorDisplay"
import { ApplicationContext } from "../applicationProvider"
import BackNextButtons from "../formGenerator/backNextButtons"
import { APPLICATION_SPACING } from "../formGenerator/formDispatcher"
import ArrayFormHelperButtons from "../formGenerator/singleArrayForm/arrayFormHelperButtons"
import { useFormGenerator } from "../formGenerator/useFormGenerator"
import InputGenerator from "../inputGenerator/inputGenerator"
import {
    DERIVED_INPUT,
    FIXED_INPUT,
    USER_ACCOUNT_DERIVED_INPUT,
} from "../recognisedInputs/recognisedInputKeys"
import SelectNewDevice, {
    SUPPLEMENTING_KEY,
} from "../recognisedInputs/selectNewDevice"
import { SupplyPhases } from "../types/formGeneratorTypes"

const SELECTED_LCT_KW = "deviceType"
const NEW_DEVICE_KW = "deviceSysRef"
const DEVICE_CLASS_KW = "deviceClass"
const DEVICE_PHASE_CODE = "phaseCode"
const DEVICE_COUNT_ROOT_ERROR =
    "The devices you have picked here do not match the technologies you selected at the beginning of this application"
export const PHASE_ROOT_ERROR =
    "The phase of one selected device is not compatible with the supply phase you have entered earlier in this form"
export const ADMD_ROOT_ERROR =
    "The demand of the devices included exceeds the maximum demand of the premises"
// Assumed properties - as the form is dynamic, it cannot rely on the the following
// inputs being present. They are assumed to be present, but used only for
// validation purposes. If the schema were to change and these properties were
// not updated, the form would still work (minus the validation)
const ASSUMED_SUPPLY_DETAILS_MERGED_KW = "supplyDetailsMerged"
const ASSUMED_SUPPLY_PHASE_CODE_KW = "phaseCode"

/**
 * PBI 71845 — returns the linked V2G inverter sysref to display in the
 * auto-resolution alert, or `null` when no alert should render.
 *
 * Conditions for a non-null return:
 *   1. The row is a V2G EVCP picker (delegated to `isV2gEvcpContext`)
 *   2. The picked device exposes a non-empty `v2gTtrStatus`
 *
 * Exported for unit testing.
 */
export const resolveLinkedV2gInverterRef = (opts: {
    lcts: string[] | undefined
    dataset: Datasets | undefined
    pickedDevice: IDeviceSearchResult | null | undefined
}): string | null => {
    const { lcts, dataset, pickedDevice } = opts
    if (!isV2gEvcpContext({ lcts, dataset })) {
        return null
    }
    const ref = pickedDevice?.v2gTtrStatus
    return ref || null
}

interface ISelectDeviceFormProps {
    formKey: string
    formProperties: ISelectDeviceFormSpec
    isNewDeviceMode: boolean
}

const SelectDeviceForm = ({
    formKey,
    formProperties,
    isNewDeviceMode,
}: ISelectDeviceFormProps): ReactElement => {
    const {
        setCurrentStepIndex,
        formData,
        setFormData,
        lcts,
        onClickBackFirstStep,
        isFirstStep,
        fixedInputs,
    } = useContext(ApplicationContext)
    const { postDraftApplication } = useSubmitDraft()
    const [searchParams, setSearchParams] = useSearchParams()

    const { email, firstName, lastName } = useGetUserData()
    const { isMob } = useScreenSize()

    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false)
    const [currentFieldIndex, setCurrentFieldIndex] = useState<number | null>(
        null
    )

    const supplyPhase =
        (
            formData?.[ASSUMED_SUPPLY_DETAILS_MERGED_KW] as {
                phaseCode?: string | number
            }
        )?.[ASSUMED_SUPPLY_PHASE_CODE_KW] ?? null

    const deviceClasses = Object.keys(
        formProperties.items.discriminator.mapping
    )
    const discriminatorKey = formProperties.items.discriminator.propertyName

    const deviceClassDispatcher = buildDeviceClassDispatcher(
        deviceClasses,
        isNewDeviceMode
    )

    // If new device mode limit the device types to the selected LCT.
    // If not (existing device mode), no limit on device types
    const lctDevices = isNewDeviceMode
        ? getDeviceTypesForSelectedLcts(lcts)
        : Object.keys(deviceClassDispatcher)

    // Generate initial values for the form
    const initialValuesArray = formData?.[formKey] as { [x: string]: object }[]
    const newDevice = {
        [SELECTED_LCT_KW]:
            lctDevices.length === 1 && isNewDeviceMode
                ? lctDevices[0]
                : undefined,
    }
    const initialValues = {
        [formKey]: initialValuesArray ?? (isNewDeviceMode ? [newDevice] : []),
    }

    // Instantiate the form register
    const { errors, ...formMethods } = useFormGenerator<typeof initialValues>(
        initialValues,
        "onSubmit"
    )

    const { fields, append, remove, update } = useFieldArray({
        control: formMethods.control,
        name: formKey as never,
    })

    const processFormValuesForUpdate = (watchValues: any): any[] =>
        watchValues[formKey].map((field: Record<string, any>) => {
            const selectedClass = field[DEVICE_CLASS_KW]
            const selectedFormSpec = formProperties.items.oneOf.find(
                (formOptionSpec) =>
                    formOptionSpec.properties[discriminatorKey]
                        .json_schema_extra.fixedValue === selectedClass
            )

            if (selectedFormSpec === undefined) {
                return field
            }

            return Object.entries(selectedFormSpec.properties).reduce<{
                [x: string]: unknown | undefined
            }>((acc, [key, value]) => {
                const inputType = value.json_schema_extra.type

                if (inputType === FIXED_INPUT) {
                    return {
                        ...acc,
                        [key]: value.json_schema_extra.fixedValue ?? "",
                    }
                }

                if (inputType === USER_ACCOUNT_DERIVED_INPUT) {
                    return processUserAccountDerivedInputs(
                        acc,
                        key,
                        value,
                        email,
                        firstName,
                        lastName
                    )
                }

                if (inputType === DERIVED_INPUT) {
                    const sourceValue =
                        field[
                            value.json_schema_extra.derivedValue?.source ?? ""
                        ] ?? ""
                    return {
                        ...acc,
                        [key]:
                            value.json_schema_extra.derivedValue
                                ?.sourceOutputMappings[sourceValue as string] ??
                            null,
                    }
                }
                return acc
            }, field)
        })

    useEffect(() => {
        const subscription = formMethods.watch((formValues) => {
            // Only process if we have actual values
            if (formValues && Object.keys(formValues).length > 0) {
                // Create a deep copy to avoid reference issues
                const formWatch = JSON.parse(JSON.stringify(formValues))

                const processedValues = processFormValuesForUpdate(formWatch)

                // Get current form values for all fields
                const currentFormData = {
                    ...formData,
                    [formKey]: processedValues,
                }

                // Preserve installer customer details if they exist
                if (formData.installerCustomerDetails) {
                    currentFormData.installerCustomerDetails =
                        formData.installerCustomerDetails
                }

                setFormData(currentFormData)
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [formKey, setFormData, formData])

    /** Called when user clicks enter manually */
    const clickEnterManually = (
        formKeyIndex: string,
        selectedDeviceKey: string
    ): void => {
        formMethods.setValue(
            `${formKeyIndex}.${DEVICE_CLASS_KW}`,
            deviceClassDispatcher[selectedDeviceKey][
                isNewDeviceMode
                    ? NOT_IN_DATABASE_KEY
                    : EXISTING_NOT_IN_DATABASE_KEY
            ] as never
        )
    }

    /** Checks if search device box should be shown */
    const showDeviceSearch = (
        selectedClass: string,
        selectedLctKey: string
    ): boolean => {
        // If class is selected, show if the class is a database class
        if (selectedClass) {
            return (
                deviceClassDispatcher[selectedLctKey][
                    isNewDeviceMode ? IN_DATABASE_KEY : EXISTING_IN_DATABASE_KEY
                ] === selectedClass
            )
        }
        // If class is not selected, show if lct is selected
        return !!selectedLctKey
    }

    /** Open advanced search for a specific field */
    const openAdvancedSearch = (index: number): void => {
        const selectedLctValue = formMethods.watch(
            `${formKey}.${index}.${SELECTED_LCT_KW}`
        )
        const selectedLctKey =
            typeof selectedLctValue === "string" ? selectedLctValue : ""
        const selectedDataset =
            deviceClassDispatcher[selectedLctKey]?.databaseName

        if (selectedDataset) {
            const nextParams = new URLSearchParams(searchParams)
            nextParams.set("dataset", selectedDataset)
            setSearchParams(nextParams)
        }

        setCurrentFieldIndex(index)
        setAdvancedSearchOpen(true)
    }

    /** Handle device selection from advanced search */
    const handleAdvancedSearchDeviceSelect = (
        device: IDeviceSearchResult
    ): void => {
        if (currentFieldIndex !== null) {
            const formKeyIndex = `${formKey}.${currentFieldIndex}`
            const selectedLctValue = formMethods.watch(
                `${formKeyIndex}.${SELECTED_LCT_KW}`
            ) as string

            try {
                // First, set the supplementing info which contains all device details
                formMethods.setValue(
                    `${formKeyIndex}.${SUPPLEMENTING_INFO_KW}`,
                    {
                        [SUPPLEMENTING_KEY]: device,
                    } as never
                )

                // Then set class and reference, which should trigger the UI to show device details
                formMethods.setValue(
                    `${formKeyIndex}.${DEVICE_CLASS_KW}`,
                    deviceClassDispatcher[selectedLctValue][
                        isNewDeviceMode
                            ? IN_DATABASE_KEY
                            : EXISTING_IN_DATABASE_KEY
                    ] as never
                )

                formMethods.setValue(
                    `${formKeyIndex}.${NEW_DEVICE_KW}`,
                    device.deviceRef as never
                )

                // Force form state update
                formMethods
                    .trigger(`${formKeyIndex}.${DEVICE_CLASS_KW}`)
                    .then(() => {
                        // After class is validated, update the parent form context
                        const currentValues = JSON.parse(
                            JSON.stringify(formMethods.getValues())
                        )

                        // Update the form data context without processing (which might add validation)
                        setFormData({
                            ...formData,
                            [formKey]: currentValues[formKey],
                        })
                    })
            } catch (err) {
                console.error("Error setting device details:", err)
            }
        }
    }

    /** Get selected device type as string safely */
    const getSelectedDeviceType = (index: number): string => {
        const value = formMethods.watch(
            `${formKey}.${index}.${SELECTED_LCT_KW}`
        )
        return typeof value === "string" ? value : ""
    }

    /** Duplicates an entry with data carried through */
    const duplicateField = (index: number): void => {
        const fieldValues = formMethods.getValues()[formKey][index]
        append(fieldValues)
    }

    // Called before form submission to clear any manual errors
    const clearManualErrors = (): void => {
        formMethods.clearErrors(DEVICE_COUNT_ROOT_ERROR)
        formMethods.clearErrors(PHASE_ROOT_ERROR)
        formMethods.clearErrors(ADMD_ROOT_ERROR)
    }

    const saveDraft = async (): Promise<void> => {
        if (formData.installerCustomerDetails) {
            postDraftApplication()
        }
    }

    /** Called when user clicks next and the form is valid validation. Any
     * errors manually set must be cleared in `clearManualErrors` before
     * calling this function
     */
    const onClickNext = (): void => {
        const formWatch = JSON.parse(JSON.stringify(formMethods.getValues()))
        formMethods.clearErrors(DEVICE_COUNT_ROOT_ERROR)
        if (supplyPhase) {
            const noPhasesErrors = formWatch[formKey].every(
                (selectedDevice: Record<string, any>) => {
                    // Selected device phases
                    const selectedDeviceClass = selectedDevice[DEVICE_CLASS_KW]
                    let selectedDevicePhases: string | number
                    if (
                        NEW_AND_EXISTING_IN_DATABASE.includes(
                            selectedDeviceClass
                        )
                    ) {
                        selectedDevicePhases =
                            selectedDevice[SUPPLEMENTING_INFO_KW][
                                SUPPLEMENTING_KEY
                            ].devicePhases
                    } else {
                        selectedDevicePhases = selectedDevice[DEVICE_PHASE_CODE]
                    }

                    if (!selectedDevicePhases) {
                        // If a code cannot be found, pass the validation.
                        // This is a precautionary measure
                        return true
                    }

                    // Split phase check
                    if (supplyPhase === SPLIT_PHASE_ASSUMED) {
                        // eslint-disable-next-line eqeqeq
                        if (selectedDevicePhases == THREE_PHASE_ASSUMED) {
                            formMethods.setError(PHASE_ROOT_ERROR, {
                                type: PHASE_ROOT_ERROR,
                                message:
                                    "You cannot connect a 3 phase device to a split phase supply",
                            })
                            return false
                        }
                        // Single phase check
                    } else if (supplyPhase === SINGLE_PHASE_ASSUMED) {
                        // eslint-disable-next-line eqeqeq
                        if (selectedDevicePhases == THREE_PHASE_ASSUMED) {
                            formMethods.setError(PHASE_ROOT_ERROR, {
                                type: PHASE_ROOT_ERROR,
                                message:
                                    "you cannot connect a 3 phase device to a single phase supply",
                            })
                            return false
                        }
                    }
                    return true
                }
            )
            if (!noPhasesErrors) {
                return
            }
        }
        if (isNewDeviceMode) {
            const selectedDevices = formWatch[formKey].map(
                (selectedDevice: Record<string, any>) =>
                    selectedDevice[SELECTED_LCT_KW]
            )

            const noDeviceErrors = lctDevices.every((lctDevice) => {
                if (!selectedDevices.includes(lctDevice)) {
                    formMethods.setError(DEVICE_COUNT_ROOT_ERROR, {
                        type: DEVICE_COUNT_ROOT_ERROR,
                        message: `at least 1 ${lctDevice} device is required for the technologies you have selected (${lcts.join(
                            ", "
                        )}). To add a ${lctDevice} to this application, press the "Add Device" button above and select one under "device type". If you no longer require this device, you will need to start the application again`,
                    })
                    return false
                }
                return true
            })

            if (!noDeviceErrors) {
                return
            }
        }

        if (isNewDeviceMode) {
            try {
                const { applicationClass } = fixedInputs
                const devicesToInstall = formWatch[formKey] as {
                    [x: string]: string | number | boolean
                }[]
                const { existingDevices, supplyDetailsMerged } = formData
                const formattedExistingDevices = existingDevices as {
                    [x: string]: string | number | boolean
                }[]
                const formattedSupplyDetails = supplyDetailsMerged as {
                    [x: string]: unknown
                }
                const { newPremisesMaxDemand, isImportLimitingDevicePresent } =
                    formattedSupplyDetails

                if (
                    !applicationIsAdmdCompliant(
                        applicationClass as ApplicationClasses,
                        formattedExistingDevices,
                        devicesToInstall,
                        parseFloat(newPremisesMaxDemand as string),
                        isImportLimitingDevicePresent as boolean
                    )
                ) {
                    formMethods.setError(ADMD_ROOT_ERROR, {
                        type: ADMD_ROOT_ERROR,
                        message:
                            "The combined demand of valid devices included in the application exceeds the premises maximum demand you entered in section 2. Please go back to section 2 and update this value accordingly",
                    })
                    return
                }
                // eslint-disable-next-line no-empty
            } catch (err) {}
        }

        const formValues = processFormValuesForUpdate(formWatch)

        // Create current form data with the latest values
        const currentFormData = {
            ...formData,
            [formKey]: formValues,
        }
        setCurrentStepIndex((prev) => prev + 1)
        saveDraft()
        setFormData(currentFormData)
        scrollToTop(document)
    }

    const rootErrorsOnly = (): { [x: string]: object | undefined } => {
        const { [formKey]: _, ...rootErrors } = errors
        return rootErrors
    }
    return (
        <FormProvider {...formMethods}>
            <Stack spacing={APPLICATION_SPACING}>
                {fields.map((field, index) => {
                    const formKeyIndex = `${formKey}.${index}`
                    const selectedClass = formMethods.watch(
                        `${formKeyIndex}.${DEVICE_CLASS_KW}`
                    )
                    const selectedLctValue = formMethods.watch(
                        `${formKeyIndex}.${SELECTED_LCT_KW}`
                    )

                    const selectLctRegisterName = `${formKeyIndex}.${SELECTED_LCT_KW}`

                    const selectedLctKey = selectedLctValue as string
                    const dataset =
                        deviceClassDispatcher[selectedLctKey]?.databaseName

                    const fieldErrors = errors[formKey]?.[index] ?? {}
                    const lctHasManualSelectOption =
                        deviceClassDispatcher[selectedLctKey]?.[
                            isNewDeviceMode
                                ? SELECT_MANUAL_OPTION_NEW
                                : SELECT_MANUAL_OPTION_EXISTING
                        ]

                    // PBI 71845 — V2G EVCP picker derivations. Centralised here
                    // (rather than inline in JSX) so manual-entry suppression
                    // and the auto-resolved-inverter alert share one source of
                    // truth and do not drift.
                    const isV2gEvcpRow = isV2gEvcpContext({ lcts, dataset })
                    const pickedV2gDevice = formMethods.watch(
                        `${formKeyIndex}.${SUPPLEMENTING_INFO_KW}.${SUPPLEMENTING_KEY}`
                    ) as IDeviceSearchResult | null | undefined
                    const linkedV2gInverterRef = resolveLinkedV2gInverterRef({
                        lcts,
                        dataset,
                        pickedDevice: pickedV2gDevice,
                    })

                    return (
                        <Stack spacing={APPLICATION_SPACING} key={field.id}>
                            <Stack
                                spacing={INPUT_HELP_TOOLTIP_SPACING}
                                direction="row"
                                alignItems="center"
                            >
                                <ValidatedAutocomplete
                                    control={formMethods.control}
                                    label="Select LCT"
                                    registerName={selectLctRegisterName}
                                    required
                                    options={lctDevices}
                                    onSelectOverride={(value) =>
                                        update(index, {
                                            [SELECTED_LCT_KW]: value,
                                        })
                                    }
                                />
                                {!isMob && (
                                    <>
                                        <AppTooltip title={TT_CANT_SEE_LCT}>
                                            <AppButton
                                                variant="outlined"
                                                sx={{ minWidth: "180px" }}
                                                onClick={onClickBackFirstStep}
                                            >
                                                Can&apos;t see your LCT?
                                            </AppButton>
                                        </AppTooltip>
                                        <HelpIcon tooltip={TT_SELECT_LCT} />
                                    </>
                                )}
                            </Stack>
                            <Stack
                                spacing={isMob ? 0 : 5}
                                direction={isMob ? "column" : "row"}
                                alignItems={isMob ? "flex-start" : "center"}
                            >
                                <Collapse
                                    in={showDeviceSearch(
                                        selectedClass,
                                        selectedLctKey
                                    )}
                                    sx={{ width: "100%" }}
                                >
                                    <Stack
                                        spacing={INPUT_HELP_TOOLTIP_SPACING}
                                        direction="row"
                                        alignItems="center"
                                    >
                                        <Box sx={{ flexGrow: 1 }}>
                                            <SelectNewDevice
                                                key={selectedLctKey}
                                                supplementingInfoKey={`${formKeyIndex}.${SUPPLEMENTING_INFO_KW}`}
                                                registerName={`${formKeyIndex}.${NEW_DEVICE_KW}`}
                                                control={formMethods.control}
                                                watch={(k) =>
                                                    formMethods.watch(k)
                                                }
                                                deviceType={selectedLctKey}
                                                dataset={dataset}
                                                phases={
                                                    supplyPhase as SupplyPhases
                                                }
                                                programmaticallySetForm={(
                                                    k,
                                                    v
                                                ) =>
                                                    formMethods.setValue(
                                                        `${formKeyIndex}.${k}`,
                                                        v as never
                                                    )
                                                }
                                                setClass={() => {
                                                    formMethods.setValue(
                                                        `${formKeyIndex}.${DEVICE_CLASS_KW}`,
                                                        deviceClassDispatcher[
                                                            selectedLctKey
                                                        ][
                                                            isNewDeviceMode
                                                                ? IN_DATABASE_KEY
                                                                : EXISTING_IN_DATABASE_KEY
                                                        ] as never
                                                    )
                                                }}
                                            />
                                        </Box>

                                        {!!selectedLctKey && (
                                            <AppTooltip
                                                title={TT_ADVANCED_SEARCH}
                                            >
                                                <AppButton
                                                    variant="outlined"
                                                    sx={{ ml: 2 }}
                                                    startIcon={<SearchIcon />}
                                                    onClick={() =>
                                                        openAdvancedSearch(
                                                            index
                                                        )
                                                    }
                                                >
                                                    Advanced Search
                                                </AppButton>
                                            </AppTooltip>
                                        )}

                                        {!isMob && (
                                            <HelpIcon
                                                tooltip={TT_DEVICE_SEARCH}
                                            />
                                        )}
                                    </Stack>
                                </Collapse>
                                <Collapse
                                    in={
                                        !!selectedLctValue &&
                                        !selectedClass &&
                                        lctHasManualSelectOption &&
                                        // PBI 71845: V2G applications must use a
                                        // registered EVCP_AC. Manual entry would
                                        // trigger V2G_EVCP_NOT_REGISTERED on
                                        // submit, so suppress it here.
                                        !isV2gEvcpRow
                                    }
                                    sx={{ mt: 0 }}
                                >
                                    <AppTooltip title={TT_ENTER_MANUALLY}>
                                        <AppButton
                                            variant="outlined"
                                            sx={{ mt: 1, minWidth: "250px" }}
                                            onClick={() =>
                                                clickEnterManually(
                                                    formKeyIndex,
                                                    selectedLctKey
                                                )
                                            }
                                        >
                                            Enter device manually
                                        </AppButton>
                                    </AppTooltip>
                                </Collapse>
                            </Stack>
                            {/* PBI 71845: when an EVCP_AC is picked in a V2G
                                application, show an info alert announcing the
                                auto-resolved V2G inverter (sourced from the
                                EVCP's V2G_TTRStatus). The inverter is attached
                                server-side; no installer input required. */}
                            {linkedV2gInverterRef && (
                                <AppAlert severity="info">
                                    <AlertTitle>
                                        Linked V2G inverter auto-selected
                                    </AlertTitle>
                                    The V2G inverter{" "}
                                    <strong>{linkedV2gInverterRef}</strong>{" "}
                                    linked to this EVCP in the ENA register has
                                    been automatically attached to your
                                    application.
                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        flexDirection="row"
                                        marginTop={2}
                                    >
                                        <AppButton
                                            endIcon={<NavigateNextIcon />}
                                            variant="outlined"
                                            onClick={() =>
                                                window.open(
                                                    SINGLE_GEN_DEVICE_NAV_LINK(
                                                        linkedV2gInverterRef
                                                    ),
                                                    "_blank"
                                                )
                                            }
                                        >
                                            View Inverter Details
                                        </AppButton>
                                    </Box>
                                </AppAlert>
                            )}
                            <Collapse in={!!selectedClass}>
                                {formProperties.items.oneOf.map(
                                    (formOptionSpec) => {
                                        const { fixedValue } =
                                            formOptionSpec.properties[
                                                discriminatorKey
                                            ].json_schema_extra

                                        return (
                                            <DisplayIf
                                                key={fixedValue}
                                                condition={
                                                    fixedValue === selectedClass
                                                }
                                            >
                                                <Stack
                                                    spacing={
                                                        APPLICATION_SPACING
                                                    }
                                                >
                                                    {prepareFormProperties(
                                                        formOptionSpec.properties
                                                    ).map(([key, value]) => (
                                                        <InputGenerator<object>
                                                            key={key}
                                                            isAmendment={false}
                                                            registerName={`${formKeyIndex}.${key}`}
                                                            inputType={
                                                                value
                                                                    .json_schema_extra
                                                                    .type
                                                            }
                                                            inputSpec={value}
                                                            watch={(k) =>
                                                                formMethods.watch(
                                                                    `${formKeyIndex}.${k}`
                                                                )
                                                            }
                                                            programmaticallySetForm={(
                                                                k,
                                                                v
                                                            ) =>
                                                                formMethods.setValue(
                                                                    `${formKey}.${index}.${k}`,
                                                                    v as never
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                    <ArrayFormErrorDisplay
                                                        errors={fieldErrors}
                                                        formProperties={
                                                            formOptionSpec.properties
                                                        }
                                                    />
                                                </Stack>
                                            </DisplayIf>
                                        )
                                    }
                                )}
                            </Collapse>
                            <ArrayFormHelperButtons
                                onClickDuplicate={() => duplicateField(index)}
                                onClickRemove={() => remove(index)}
                                showRemoveButton={
                                    fields.length > formProperties.minItems
                                }
                            />
                            <Divider />
                        </Stack>
                    )
                })}
                <Box display="inline-block">
                    <AppButton
                        onClick={() => append(newDevice)}
                        endIcon={<AddIcon />}
                        variant="outlined"
                    >
                        Add Device
                    </AppButton>
                </Box>
                <ArrayFormGlobalErrorDisplay formErrors={errors[formKey]} />
                <FormErrors
                    errors={rootErrorsOnly()}
                    getFriendlyName={(key: string) => key}
                />
                <BackNextButtons
                    onClickNext={() => {
                        clearManualErrors()
                        formMethods.handleSubmit(onClickNext)()
                    }}
                    setCurrentStepIndex={setCurrentStepIndex}
                    isFirstStep={isFirstStep}
                    onClickBackFirstStep={onClickBackFirstStep}
                />
            </Stack>

            {/* Device search dialog (replaces legacy advanced search popup) */}
            {currentFieldIndex !== null && (
                <DeviceSearchDialog
                    open={advancedSearchOpen}
                    onClose={() => {
                        setAdvancedSearchOpen(false)
                        setCurrentFieldIndex(null)
                    }}
                    dataset={
                        deviceClassDispatcher[
                            getSelectedDeviceType(currentFieldIndex)
                        ]?.databaseName
                    }
                    phases={supplyPhase as SupplyPhases}
                    onDeviceSelected={handleAdvancedSearchDeviceSelect}
                />
            )}
        </FormProvider>
    )
}

export default SelectDeviceForm
